import { useEffect, useState } from "react";
import { Loader2, Mail, Send } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { intelApi } from "../../lib/intelApi";

export default function DigestPreviewDialog({ open, onOpenChange }) {
  const [html, setHtml] = useState("");
  const [next, setNext] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    Promise.all([intelApi.digestPreview(), intelApi.digestNext().catch(() => null)])
      .then(([h, n]) => {
        setHtml(h);
        setNext(n);
      })
      .catch(() => toast.error("Could not load digest preview."))
      .finally(() => setLoading(false));
  }, [open]);

  const send = async () => {
    setSending(true);
    try {
      const r = await intelApi.sendDigest();
      toast.success(`Digest emailed — ${r.subject}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send digest.");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl w-[95vw] h-[88vh] flex flex-col rounded-none border-border bg-background p-0" data-testid="digest-preview-dialog">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-border">
          <DialogTitle className="font-heading font-black uppercase tracking-tight text-2xl">
            Digest <span className="text-accent">preview</span>
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground" data-testid="digest-next-run">
            {next?.next_run
              ? `Next automatic send: ${new Date(next.next_run).toLocaleString("en-US", { timeZone: next.timezone, weekday: "short", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })} (${next.timezone}) → ${next.to}. Preview reflects the last 7 days as of now.`
              : "Preview reflects the last 7 days as of now."}
          </DialogDescription>
        </DialogHeader>
        <div className="flex-1 min-h-0 bg-[#F8FAFC]">
          {loading ? (
            <div className="h-full grid place-items-center text-muted-foreground">
              <Loader2 className="animate-spin" size={20} />
            </div>
          ) : (
            <iframe title="Digest preview" srcDoc={html} sandbox="" className="w-full h-full border-0" data-testid="digest-preview-frame" />
          )}
        </div>
        <div className="px-6 py-4 border-t border-border flex flex-wrap items-center justify-between gap-3">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground inline-flex items-center gap-2">
            <Mail size={12} className="text-accent" /> Sent via the portfolio's email service
          </span>
          <button
            type="button"
            data-testid="digest-send-now-button"
            onClick={send}
            disabled={sending || loading}
            className="inline-flex items-center gap-2 bg-accent text-accent-foreground px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] hover:bg-foreground hover:text-background transition-colors disabled:opacity-60"
          >
            {sending ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Send now
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
